import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { NAVIGATION_HEADER_IMAGES } from '@/lib/images';
import SimpleSwiper from '@/components/simple-swiper';
import { useAppEnv } from '@/hooks/useAppEnv';
import { CONTAINER_PLATFORM_TYPE, APP_VERSION, CODE_ENV, CURRENT_CODE_ENV, isServer } from '@/lib/constant';
import { useLocationInfo, useUserInfo } from '@/context/PageStateContext';
import { useTracker } from '@/context/TrackerContext';
import { getGuessSearch } from '@/api/search';
import { appendQuanquanSTParams, appendUrlParams } from '@/lib/urlParams';
import { JumpTypeEnum } from '@/lib/jumpPage'
import { useJumpToPage } from '@/hooks/useJumpToPage'
import closePage from '@/lib/bridge/closePage'
import './index.scss';
import Exposure from '@/components/exposure';
import getUnpl from '@/lib/getUnpl';
import { Level, reportCustomMetric, reportError } from '@/lib/owl';
import { ArrowDownIcon, BackIcon } from './icon';
import { BANNER_HEIGHT } from '@/pages/waimai/constants';
const CITY_SOURCE = 'distribution-center';

const reportJumpWithUnpl = (hasUnpl: boolean, source: string) => {
  reportCustomMetric({
    key: 'jumpWithUnpl',
    value: hasUnpl ? 1 : 0,
    tags: {
      source
    }
  })
}

// 城市选择页
const ADDRESS_URL_MAP = {
  [CODE_ENV.DEVELOP]: 'http://h5-peppermall.mall.test.sankuai.com/pages/searchCity/index',
  [CODE_ENV.TEST]: 'http://h5-peppermall.mall.test.sankuai.com/pages/searchCity/index',
  [CODE_ENV.STAGING]: 'https://h5.peppermall.meituan.com/pages/searchCity/index',
  [CODE_ENV.PRODUCTION]: 'https://h5.peppermall.meituan.com/pages/searchCity/index'
}

const SEARCH_ENV_MAP = {
  [CODE_ENV.DEVELOP]: 'https://h5-peppermall.mall.test.sankuai.com/pages/search/index',
  [CODE_ENV.TEST]: 'https://h5-peppermall.mall.test.sankuai.com/pages/search/index',
  [CODE_ENV.STAGING]: 'https://h5.peppermall.meituan.com/pages/search/index',
  [CODE_ENV.PRODUCTION]: 'https://h5.peppermall.meituan.com/pages/search/index'
}
// 搜索页基础URL
const SEARCH_PAGE_URL = SEARCH_ENV_MAP[CURRENT_CODE_ENV as CODE_ENV];
// 搜索页URL MAP
const SEARCH_URL_MAP: Partial<Record<CONTAINER_PLATFORM_TYPE, string>> = {
  [CONTAINER_PLATFORM_TYPE.MT_MINI]: '/pepper/pages/search/index',
  [CONTAINER_PLATFORM_TYPE.MT_APP]: `imeituan://www.meituan.com/mmp`,
  [CONTAINER_PLATFORM_TYPE.BROWSER]: SEARCH_PAGE_URL,
}

interface NavigationHeaderProps {
  showSearchBar?: boolean; // 是否展示搜索栏
  navTextColor?: string; // 导航栏文本颜色
  enableNavBarBgOpacity?: boolean; // 是否开启导航栏背景色透明度变化
  lxConfig?: { // 埋点配置
    NAVIGATION_HEADER_BID_MV: {
      LOCATION: string;
      SEARCH_INPUT?: string;
      SEARCH_WORD?: string;
    };
    NAVIGATION_HEADER_BID_MC: {
      LOCATION: string;
      SEARCH_BUTTON?: string;
    };
  };
};

const NavigationHeader: React.FC<NavigationHeaderProps> = function (props) {
  const { 
    showSearchBar = true,
    navTextColor = '#FFFFFF',
    lxConfig,
    enableNavBarBgOpacity = false,
  } = props;
  const [searchWords, setSearchWords] = useState<string[]>([]);
  const [currentSearchWordIndex, setCurrentSearchWordIndex] = useState(0);
  const [navBarOpacity, setNavBarOpacity] = useState(enableNavBarBgOpacity ? 0 : 1); // 背景颜色层的透明度
  const { locationInfo } = useLocationInfo();
  const { selectCityInfo, isInited } = locationInfo || {}
  const { userInfo } = useUserInfo();
  const tracker = useTracker();
  const reportedKeywordsRef = useRef<Set<number>>(new Set()); // 记录已上报的关键词索引
  const jumpToPage = useJumpToPage();

  // 使用 useAppEnv Hook 兼容 SSR/CSR 环境检测
  const appEnv = useAppEnv();

  // 定位入口曝光埋点
  const handleLocationExpose = useCallback(() => {
    if (tracker && lxConfig?.NAVIGATION_HEADER_BID_MV?.LOCATION) {
      tracker.moduleView(lxConfig.NAVIGATION_HEADER_BID_MV.LOCATION);
    }
  }, [tracker, lxConfig]);
  // 搜索输入框曝光埋点
  const handleSearchInputExpose = useCallback(() => {
    if (tracker && lxConfig?.NAVIGATION_HEADER_BID_MV?.SEARCH_INPUT) {
      tracker.moduleView(lxConfig.NAVIGATION_HEADER_BID_MV.SEARCH_INPUT);
    }
  }, [tracker, lxConfig]);

  // 定位文案
  const locationText = useMemo(() => {
    // SSR 展示和 CSR 初始化文案需要保持相同
    if (!isInited || isServer) return '定位中...'
    if (selectCityInfo && selectCityInfo.cityName) {
      return selectCityInfo.cityName
    }
    return '请手动定位'
  }, [selectCityInfo, isInited, locationInfo]);

  // 是否展示返回按钮
  const showBackButton = useMemo(() => {
    return appEnv.containerPlatform === CONTAINER_PLATFORM_TYPE.MT_APP
  }, [appEnv.containerPlatform]);

  /**
   * 返回按钮点击处理
  */
  const handleBackClick = () => {
    closePage()
  };

  /**
   * 地址栏点击处理
  */
  const handleLocationClick = () => {
    // 上报定位入口点击埋点
    if (tracker && lxConfig?.NAVIGATION_HEADER_BID_MC?.LOCATION) {
      tracker.moduleClick(lxConfig.NAVIGATION_HEADER_BID_MC.LOCATION);
    }
    const currentPageUrl = window.location.href;
    const ADDRESS_URL = ADDRESS_URL_MAP[CURRENT_CODE_ENV as CODE_ENV]
    const jumpLink = appendUrlParams(ADDRESS_URL, appendQuanquanSTParams({
      scene: CITY_SOURCE,
      currentCityId: `${selectCityInfo?.cityId}`,
      backUrl: currentPageUrl,
    }));
    jumpToPage({
      jumpLink,
      jumpType: JumpTypeEnum.H5,
      isRedirect: true,
      source: 'locationBar',
    })
  };

  /**
   * 搜索点击处理
   * 根据不同环境执行相应的跳转逻辑
   * @param isSearchBtn 是否为搜索按钮点击
   */
  const handleSearchClick = (isSearchBtn: boolean) => {
    // 从 CONTAINER_PLATFORM_TYPE 中取出当前环境对应的 URL
    let searchUrl = SEARCH_URL_MAP[appEnv.containerPlatform] || SEARCH_PAGE_URL;

    // ST 环境增加圈圈白名单参数
    const commonParams: Record<string, string> = appendQuanquanSTParams({
      source: '3', // 标识分销中心页场景，用于搜索页商卡展示最大可用券标签，跟单以及城市切换
      channelType: 'WANG_MENG', // 走网盟跟单链路
    });
    if (selectCityInfo?.cityId) {
      commonParams.pushCityId = `${selectCityInfo.cityId}`; // 取手选城市ID
    }
    // 仅 APP 环境向搜索页传递 unpl
    // H5 环境可以从 cookie 中读取
    // 小程序环境访问 click 中间页时，会在小程序侧请求 mini 接口种 unpl 到小程序的 Storage 中，商详页可以从 Storage 中获取 unpl
    if (!appEnv.isBrowser || !appEnv.isMTWXApp) {
      commonParams.unpl = getUnpl() || ''; // 取值 unpl
      reportJumpWithUnpl(Boolean(commonParams.unpl), 'searchBar');
    }

    // 获取当前轮播的搜索词
    if (isSearchBtn) {
      // 上报搜索按钮点击埋点
      const currentSearchWord = searchWords.length > 0 
        ? searchWords[currentSearchWordIndex % searchWords.length] 
        : '';
      commonParams.defaultSearchWord = currentSearchWord;
      // 上报搜索按钮点击埋点
      if (tracker && lxConfig?.NAVIGATION_HEADER_BID_MC?.SEARCH_BUTTON) {
        tracker.moduleClick(lxConfig.NAVIGATION_HEADER_BID_MC.SEARCH_BUTTON, {
          keyword: currentSearchWord,
        });
      }
    }

    // APP 环境拼接参数
    if (appEnv.containerPlatform === CONTAINER_PLATFORM_TYPE.MT_APP) {
      const params = {
        appId: 'bca74ebe2fc448e0',
        targetPath: appendUrlParams('/pages/search/index', commonParams),
      }
      searchUrl = appendUrlParams(searchUrl, params);
    } else {
      searchUrl = appendUrlParams(searchUrl, commonParams);
    }
    // sessionId 未绑定时展示 Loading，绑定后自动跳转
    jumpToPage({
      jumpLink: searchUrl,
      jumpType: appEnv.containerPlatform as unknown as JumpTypeEnum,
      source: 'searchBar',
    })
  };

  // 获取搜索词
  useEffect(() => {
    // 🆕 搜索框不展示时，不获取搜索词
    if (!showSearchBar) return;
    const fetchSearchWords = async () => {
      try {
        const { actualCityInfo, selectCityInfo, isInited: locationInited } = locationInfo || {}
        const { userId, openId } = userInfo || {}
        if (!locationInited) return
        const params = {
          selectCityId: selectCityInfo?.cityId,
          cityId: selectCityInfo?.cityId,
          userId: userId ? Number(userId) : undefined,
          _ver: APP_VERSION,
          platform: appEnv.containerPlatform,
          lat: actualCityInfo?.lat,
          lng: actualCityInfo?.lng,
          useForPrompt: true, // 用于文案提示（会过滤+截取3个）
          wxOpenId: openId || '', // 微信openid
          fromHome: true, // 是否从首页进入
          sourceFlagCode: 16, // 写死
          channelSource: 'search',
        };
        if (!params.wxOpenId && !params.userId) {
          // 使用lx cuid 补充为openId
          params.wxOpenId = await tracker?.getCuid().catch(()=> '') || ''
        }
        const result = await getGuessSearch(params);
        if (result.code !== 200) return
        const words = result.data?.searchWords?.map((item: { word: string }) => item.word) || []
        if(words.length < 1) return
        setSearchWords(words);
        // 搜索词列表更新时，清空已上报记录
        reportedKeywordsRef.current.clear();
      } catch (error) {
        reportError({
          name: '[navigationHeader] fetchSearchWordsError',
          msg: JSON.stringify(error),
          category: 'jsError',
          level: Level.ERROR,
        })
      }
    };
    fetchSearchWords();
  }, [locationInfo, userInfo, showSearchBar]);

  // 开启导航栏背景色透明度变化时，监听页面滚动事件，设置导航栏的背景透明度
  useEffect(() => {
    if (!enableNavBarBgOpacity) return;

    // 节流函数：限制函数执行频率
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        // 获取头图高度
        const bannerHeight = BANNER_HEIGHT;
        // 计算滚动距离
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        // 计算透明度：滚动距离 / 头图高度，确保在0-1之间
        const opacity = Math.min(1, Math.max(0, scrollTop / bannerHeight));
        setNavBarOpacity(opacity);
        ticking = false;
      });
    };

    // 监听滚动事件
    window.addEventListener('scroll', handleScroll, { passive: true });
    // 初始触发一次，确保状态正确
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [enableNavBarBgOpacity]);

  // 轮播索引变化回调
  const handleSwiperChange = useCallback((index: number) => {
    setCurrentSearchWordIndex(index);
    // 检查是否已经上报过该关键词，避免重复上报
    if (reportedKeywordsRef.current.has(index)) {
      return;
    }
    // 记录已上报的索引
    reportedKeywordsRef.current.add(index);
    // 上报搜索词轮播埋点
    if (tracker && lxConfig?.NAVIGATION_HEADER_BID_MV?.SEARCH_WORD) {
      tracker.moduleView(lxConfig.NAVIGATION_HEADER_BID_MV.SEARCH_WORD, {
        keyword: searchWords[index] || '',
      });
    }
  }, [tracker, lxConfig, searchWords]);

  return (
    <>
      {/* 背景颜色 */}
      <div className="page-header-bg" style={{ opacity: navBarOpacity }}></div>
      <div className="navigation-header">
        {/* 左侧区域 */}
        <Exposure onExpose={handleLocationExpose}>
          <div className="navigation-header-left">
            {/* 返回按钮 */}
            {showBackButton && (
              <div className="navigation-header-back" onClick={handleBackClick}>
                <i className="navigation-header-back-img" style={{ color: navTextColor }}><BackIcon /></i>
              </div>
            )}
            
            {/* 地址区域 */}
            <div className="navigation-header-location" onClick={handleLocationClick}>
              <span className="navigation-header-location-text" style={{ color: navTextColor }}>{locationText}</span>
              <i className="navigation-header-location-arrow" style={{ color: navTextColor }}><ArrowDownIcon /></i>
            </div>
          </div>
        </Exposure>
        {/* 右侧搜索框 */}
        {/* 🆕 增加条件：仅在展示搜索栏时才渲染 */}
        {showSearchBar && (
          <Exposure onExpose={handleSearchInputExpose}>
            <div className="navigation-header-search">
              {/* 搜索图标 */}
              <img className="navigation-header-search-icon" src={NAVIGATION_HEADER_IMAGES.search} />
              
              {/* 搜索词轮播 */}
              <div className="navigation-header-search-word-container" onClick={() => { handleSearchClick(false) }}>
                <SimpleSwiper
                  direction="vertical"
                  autoplay
                  loop
                  autoplayInterval={3000}
                  onIndexChange={handleSwiperChange}
                >
                  {searchWords.length > 0 ? searchWords.map((word, index) => (
                    <SimpleSwiper.Item key={index}>
                      <span className="navigation-header-search-word">
                        {word}
                      </span>
                    </SimpleSwiper.Item>
                  )) : (
                    <SimpleSwiper.Item key="default">
                      <span className="navigation-header-search-word">
                        搜索更多
                      </span>
                    </SimpleSwiper.Item>
                  )}
                </SimpleSwiper>
              </div>
              
              {/* 搜索按钮 */}
              <div className="navigation-header-search-btn" onClick={() => { handleSearchClick(true) }}>搜索</div>
            </div>
          </Exposure>
        )}
      </div>
    </>
  );
};

export default NavigationHeader;
